package diana.soleil.design.iraniancanadienbazzar.core.model

/**
 * Phase-1 "appeal-first" discovery ranking, shared by every list surface (Home, directory lists,
 * search, events, news, feed). One deliberate place to change how the app orders content:
 *
 *   1. Listings with a REAL photo first — placeholder-art cards sink so first impressions sell.
 *   2. Then nearby — coarse distance bands from the user's GPS fix (or the active metro's centre
 *      when location is unknown/denied), so "2 km vs 4 km" doesn't outweigh quality…
 *   3. …then rating (with community endorsements and review count as tiebreakers) within a band.
 *
 * This is a launch-phase appeal heuristic, not the forever algorithm — engagement/freshness signals
 * replace it later. Keep every list ordering flowing through here so the swap is one change.
 */
object DiscoveryRanking {

    /** True when a card will render an actual photo rather than the category placeholder art. */
    val Business.hasRealPhoto: Boolean
        get() = mediaStatus == MediaStatus.OwnerMedia ||
            !coverUrl.isNullOrBlank() ||
            photos.any { it.isNotBlank() }

    /**
     * Where "nearby" is measured from: the user's GPS fix when granted, else the active metro's
     * centre, else null (distance is ignored and the ranking falls through to rating).
     */
    fun origin(userLocation: GeoPoint?, metroId: String?): GeoPoint? =
        userLocation ?: Metros.byId(metroId)?.let { GeoPoint(it.centerLat, it.centerLng) }

    // Band edges in km. Coarse on purpose: within a band, rating decides — a 4.9★ restaurant 4 km
    // away should beat a 3.1★ one 2 km away, but a different part of town should still rank lower.
    private val DISTANCE_BANDS_KM = doubleArrayOf(2.0, 5.0, 10.0, 25.0, 50.0)

    /** Band index for [km] (0 = closest); null/unknown distances land in the last band. */
    fun distanceBand(km: Double?): Int =
        if (km == null) DISTANCE_BANDS_KM.size + 1
        else DISTANCE_BANDS_KM.indexOfFirst { km < it }.let { if (it == -1) DISTANCE_BANDS_KM.size else it }

    /** The default business ordering: real photo → distance band from [origin] → rating. */
    fun businessComparator(origin: GeoPoint?): Comparator<Business> =
        compareByDescending<Business> { it.hasRealPhoto }
            .thenBy { business -> distanceBand(origin?.let { business.distanceKmFrom(it) }) }
            .thenByDescending { it.rating }
            .thenByDescending { it.endorsementCount }
            .thenByDescending { it.reviewCount }
            .thenBy { it.name.lowercase() }

    /** Exact-distance ordering for an explicit "Nearest" sort; un-geocoded listings sink last. */
    fun businessNearestComparator(origin: GeoPoint?): Comparator<Business> =
        compareBy<Business, Double?>(nullsLast()) { business -> origin?.let(business::distanceKmFrom) }
            .thenByDescending { it.hasRealPhoto }
            .thenByDescending { it.rating }

    /** Events: real cover art first, then soonest (they carry no coordinates or rating). */
    fun eventComparator(): Comparator<Event> =
        compareByDescending<Event> { !it.coverUrl.isNullOrBlank() }
            .thenBy { it.startsAtMillis }

    /** News: articles with an image first, then newest. */
    fun newsComparator(): Comparator<NewsArticle> =
        compareByDescending<NewsArticle> { !it.imageUrl.isNullOrBlank() }
            .thenByDescending { it.publishedAtMillis }

    /** Feed "with photos" sort: photo posts first, then newest. */
    fun postPhotoFirstComparator(): Comparator<Post> =
        compareByDescending<Post> { !it.photoUrl.isNullOrBlank() }
            .thenByDescending { it.createdAtMillis ?: 0L }
}
